import { useEffect, useState } from "react";
import { apiGet } from "../api/client";
import { subscribeLive } from "../aim/liveBus";
import type { RoundCurrentTarget } from "../types/api";

const apiBase = (
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_AIM_API_BASE || ""
).replace(/\/$/, "");

export interface RoundSummaryRow {
  targetId: string;
  displayName: string | null;
  carrierId: string | null;
  zone: string | null;
  lastDetection: string | null;
}

interface TargetStateShape {
  zone?: string | null;
  lastDetection?: string | null;
}

// Compact "N OBJETIVOS ACTIVOS" overview (P3): one row per target in the
// current LIVE round, each fetched independently from the same public
// per-target state endpoint the selected-target KPIs already use.
// Deliberately Promise.allSettled, not Promise.all: each row describes a
// DIFFERENT target, so one target's fetch failing must not blank the
// others — unlike useNetwork's single-target Promise.all, which is a
// coherent snapshot of ONE target and stays all-or-nothing on purpose (see
// docs/PORTAL_LIVE_REFRESH.md).
export function useRoundSummary(targets: RoundCurrentTarget[], enabled: boolean) {
  const [rows, setRows] = useState<RoundSummaryRow[]>([]);
  const targetsKey = targets.map((t) => t.targetId).join(",");

  useEffect(() => {
    if (!enabled || targets.length === 0) {
      setRows([]);
      return;
    }
    let stopped = false;
    const abort = new AbortController();
    async function refresh() {
      const results = await Promise.allSettled(
        targets.map((t) =>
          apiGet<TargetStateShape | null>(
            `/api/v1/targets/${encodeURIComponent(t.targetId)}/state`,
            abort.signal,
          ),
        ),
      );
      if (stopped) return;
      setRows(
        targets.map((t, i) => {
          const result = results[i];
          const state = result.status === "fulfilled" ? result.value : null;
          return {
            targetId: t.targetId,
            displayName: t.displayName,
            carrierId: t.carrierId,
            zone: state?.zone ?? null,
            lastDetection: state?.lastDetection ?? null,
          };
        }),
      );
    }
    void refresh();
    const unsubscribe = subscribeLive(apiBase, () => void refresh());
    return () => {
      stopped = true;
      abort.abort();
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- targetsKey is the stable identity of `targets`
  }, [enabled, targetsKey]);

  return rows;
}
